import {
  useRouteBodyEditorSelection,
  useRouteBodyState,
  useRouteBodyRef,
} from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { MnemonicMarkdownRenderer } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.description";
import { MorphemeMnemonicsPanelContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content._context";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  alertPremiumUnavailable,
  GRAPHEME_DESCRIPTION_SUPERWALL_PLACEMENT,
  useIsPremium,
  useIsSignedIn,
  useUpgradeToPremium,
} from "@-/dev.improve.haniverse.components.superwall";
import {
  buildPremiumLockedPrompt,
  buildRevealedMnemonicUpsell,
  resolveMnemonicGateState,
  shouldShowRevealedMnemonicUpsell,
} from "@-/dev.improve.haniverse.lib.morpheme.mnemonic-gate";
import { useIsMnemonicGateReferenceSurface } from "@-/dev.improve.haniverse.lib.morpheme.mnemonic-gate-surface";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import {
  useResetGraphemeMeaningCard,
  useUpsertGraphemeMeaningCard,
} from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.meaning.update";
import { parseGraphemeFromDescriptionLinkUrl } from "@-/dev.improve.haniverse.lib.morpheme.description";
import { useGraphemeLinkPress } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.preview-sheet";
import { MorphemeMnemonicsMeaningViewContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { BlurView } from "@-/com.npmjs.expo-blur";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { useCallback, useContext, useEffect, useRef } from "react";
import { TextInput, View } from "@-/com.npmjs.react-native";
import { LongPressable } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { useAdminAccess } from "@-/dev.improve.haniverse.lib.admin.access";
import { AbsentMnemonicFallbackRenderer } from "#absent-mnemonic-fallback-renderer";
import {
  buildAbsentMnemonicSubtext,
  isGraphemeInLevelsCurriculum,
} from "#build-absent-mnemonic-description";
import { MnemonicSuggestionButton } from "#mnemonic-suggestion-button";
import { useAbsentMnemonicDescription } from "#use-absent-mnemonic-description";

const UniwindBlurView = withUniwind(BlurView);

export function MorphemeMnemonicsMeaningViewMnemonicSection() {
  const isAdmin = useAdminAccess();
  const [grapheme, mnemonicFromCatalog, isDescriptionPremium, mnemonicFromActor] =
    useContextSelectors(
      GraphemeMorphemePanelsContext,
      (c) => c.readonlySlice.data.grapheme,
      (c) => c.readonlySlice.data.mnemonic?.trim() ?? "",
      (c) => c.readonlySlice.data.isDescriptionPremium,
      (c) => c.userSlice.data.meaningCard?.description?.trim() ?? "",
    );
  const { markdown: absentMnemonicDescription } = useAbsentMnemonicDescription();
  const isPremium = useIsPremium();
  const isSignedIn = useIsSignedIn();
  const [hasRevealed, setHasRevealed] = useRouteBodyState("mnemonic-revealed", false);
  const isReferenceSurface = useIsMnemonicGateReferenceSurface();
  const { openUpgradeToPremium } = useUpgradeToPremium(
    GRAPHEME_DESCRIPTION_SUPERWALL_PLACEMENT,
    "mnemonic_description",
    { onUnavailable: alertPremiumUnavailable },
  );
  const upsertGraphemeMeaningCard = useUpsertGraphemeMeaningCard();
  const resetGraphemeMeaningCard = useResetGraphemeMeaningCard();
  const isSaving = upsertGraphemeMeaningCard.isPending || resetGraphemeMeaningCard.isPending;
  const saveError = upsertGraphemeMeaningCard.error ?? resetGraphemeMeaningCard.error;
  const [isEditing, setIsEditing] = useContextSelectors(
    MorphemeMnemonicsPanelContext,
    (c) => c.isEditing,
    (c) => c.setIsEditing,
  );
  const draftRef = useRouteBodyRef("mnemonic-draft", "");
  const wasEditingRef = useRouteBodyRef("mnemonic-was-editing", false);
  const inputRef = useRef<TextInput | null>(null);
  const editorSelection = useRouteBodyEditorSelection(inputRef, "mnemonic-editor");
  const { morphemeHrefObject, openCompoundsSheet } = useContext(
    MorphemeMnemonicsMeaningViewContext,
  );
  const prefix = morphemeHrefObject;
  const onGraphemeLinkPress = useGraphemeLinkPress({ prefix });
  const startEditing = () => {
    if (!isAdmin) {
      return;
    }
    upsertGraphemeMeaningCard.reset();
    resetGraphemeMeaningCard.reset();
    setIsEditing(true);
  };

  const displayedMnemonic = mnemonicFromActor.length > 0 ? mnemonicFromActor : mnemonicFromCatalog;
  const gateState = resolveMnemonicGateState({
    hasCatalogMnemonic: mnemonicFromCatalog.length > 0,
    hasOwnMnemonic: mnemonicFromActor.length > 0,
    hasRevealed,
    isDescriptionPremium,
    isPremium,
    isReferenceSurface,
    isSignedIn,
  });
  // A locked mnemonic must never seed the editor — that would hand the gated text back through
  // the edit field. `premium-locked` additionally has no text on the client at all.
  const isCatalogDescriptionGated = gateState === "premium-locked" || gateState === "revealable";
  const editableMnemonic = isCatalogDescriptionGated
    ? ""
    : displayedMnemonic || absentMnemonicDescription;

  useEffect(() => {
    if (isEditing && !wasEditingRef.current) {
      draftRef.current = editableMnemonic;
    }
    wasEditingRef.current = isEditing;
  }, [editableMnemonic, isEditing, draftRef, wasEditingRef]);

  const handleLinkPress = useCallback(
    ({ url }: { url: string }) => {
      const linkedGrapheme = parseGraphemeFromDescriptionLinkUrl(url);
      if (linkedGrapheme === null) {
        return;
      }
      if (linkedGrapheme === grapheme) {
        openCompoundsSheet();
        return;
      }
      onGraphemeLinkPress(linkedGrapheme);
    },
    [grapheme, onGraphemeLinkPress, openCompoundsSheet],
  );

  if (isAdmin && isEditing) {
    // Tapping Save/Cancel/Reset doesn't blur the still-focused input on native, so
    // the keyboard lingers after the input unmounts. Blur it explicitly to dismiss
    // the keyboard as editing ends (mirrors the notes section).
    const stopEditing = () => {
      void inputRef.current?.blur();
      setIsEditing(false);
    };
    const handleSave = () => {
      upsertGraphemeMeaningCard.mutate(
        { description: draftRef.current, grapheme },
        { onSuccess: stopEditing },
      );
    };
    const handleCancel = () => {
      stopEditing();
    };
    const handleReset = () => {
      resetGraphemeMeaningCard.mutate({ grapheme }, { onSuccess: stopEditing });
    };
    return (
      <View className="gap-2">
        <View className="rounded-xs border border-border bg-muted/30 p-1">
          <TextInput
            accessibilityLabel="Mnemonic Markdown"
            autoFocus
            className="min-h-[120px] p-2 text-[14px] leading-7 text-foreground"
            defaultValue={wasEditingRef.current ? draftRef.current : editableMnemonic}
            editable={!isSaving}
            multiline
            onChangeText={(markdown) => {
              draftRef.current = markdown;
            }}
            onLayout={editorSelection.onLayout}
            onSelectionChange={({ nativeEvent }) => {
              editorSelection.onChangeSelection(nativeEvent.selection);
            }}
            placeholder="Write your mnemonic for this character. Markdown is shown as plain text."
            ref={inputRef}
            testID="mnemonic-markdown-input"
            textAlignVertical="top"
          />
        </View>
        {saveError ? (
          <Text accessibilityRole="alert" className="text-sm text-destructive">
            Could not save mnemonic. {saveError.message}
          </Text>
        ) : null}
        <View className="flex-row justify-end gap-1">
          <Button disabled={isSaving} onPress={handleReset} size="sm" variant="ghost">
            <Text>Reset</Text>
          </Button>
          <Button disabled={isSaving} onPress={handleCancel} size="sm" variant="ghost">
            <Text>Cancel</Text>
          </Button>
          <Button disabled={isSaving} onPress={handleSave} size="sm" variant="ghost">
            <Text>{isSaving ? "Saving…" : "Save"}</Text>
          </Button>
        </View>
      </View>
    );
  }

  // A FREE mnemonic the viewer is entitled to but hasn't revealed. Reference page only: the text
  // is already on the device, so revealing is instant — the button exists to create the moment
  // for the upsell to that page's search visitors.
  if (gateState === "revealable") {
    return (
      <LongPressable
        accessible={false}
        className="gap-3"
        onLongPress={isAdmin ? startEditing : undefined}
        testID="meaning-mnemonic-section"
      >
        <View className="relative overflow-hidden rounded-xs border border-border bg-muted/30 p-4">
          <Text className="text-[14px] leading-7" colorVariant="default">
            A mnemonic is available for this character.
          </Text>
          <UniwindBlurView
            accessibilityElementsHidden
            blurMethod="dimezisBlurViewSdk31Plus"
            className="absolute inset-0"
            importantForAccessibility="no-hide-descendants"
            intensity={55}
            pointerEvents="none"
            tint="systemMaterial"
          />
        </View>
        <Button
          className="self-center"
          onPress={() => {
            setHasRevealed(true);
          }}
          testID="reveal-grapheme-description-button"
        >
          <Text>Reveal mnemonic</Text>
        </Button>
      </LongPressable>
    );
  }

  // A PREMIUM mnemonic. No reveal button: the text is stripped from the client database, so
  // there is nothing local to reveal — only an unlock to offer.
  if (gateState === "premium-locked") {
    return (
      <LongPressable
        accessible={false}
        className="gap-3"
        onLongPress={isAdmin ? startEditing : undefined}
        testID="meaning-mnemonic-section"
      >
        <View className="relative overflow-hidden rounded-xs border border-border bg-muted/30 p-4">
          <Text className="text-[14px] leading-7" colorVariant="default">
            This character decomposes into meaningful parts that make its story easier to remember.
            Each component contributes a vivid clue to the full mnemonic.
          </Text>
          <UniwindBlurView
            accessibilityElementsHidden
            blurMethod="dimezisBlurViewSdk31Plus"
            className="absolute inset-0"
            importantForAccessibility="no-hide-descendants"
            intensity={55}
            pointerEvents="none"
            tint="systemMaterial"
          />
        </View>
        <Text
          className="text-center text-[14px] leading-7"
          colorVariant="default"
          testID="premium-grapheme-description-prompt"
        >
          {buildPremiumLockedPrompt(grapheme)}
        </Text>
        <Button
          className="self-center"
          onPress={openUpgradeToPremium}
          testID="upgrade-grapheme-description-button"
        >
          <Text>{isSignedIn ? "Upgrade to view mnemonic" : "Sign in"}</Text>
        </Button>
      </LongPressable>
    );
  }

  if (displayedMnemonic) {
    return (
      <LongPressable
        accessible={false}
        className="gap-3"
        onLongPress={isAdmin ? startEditing : undefined}
        testID="meaning-mnemonic-section"
      >
        <MnemonicMarkdownRenderer
          grapheme={grapheme}
          mnemonic={displayedMnemonic}
          onLinkPress={handleLinkPress}
        />
        {/* Pitch the account only right after a guest reveals — that is the moment the value
            is concrete. Never shown to signed-in readers, who have already converted, nor on any
            surface that offers no reveal button to have just been tapped. */}
        {shouldShowRevealedMnemonicUpsell({ gateState, isReferenceSurface, isSignedIn }) ? (
          <Text
            className="text-[13px] leading-6 text-muted-foreground"
            testID="reveal-grapheme-description-upsell"
          >
            {buildRevealedMnemonicUpsell()}
          </Text>
        ) : null}
      </LongPressable>
    );
  }

  return (
    <LongPressable
      accessible={false}
      className="gap-3"
      onLongPress={isAdmin ? startEditing : undefined}
      testID="meaning-mnemonic-section"
    >
      <AbsentMnemonicFallbackRenderer />
      <Text
        className="text-[14px] leading-7 text-muted-foreground"
        testID="absent-mnemonic-subtext"
      >
        {buildAbsentMnemonicSubtext({
          canEdit: isAdmin,
          isInLevelsCurriculum: isGraphemeInLevelsCurriculum(grapheme),
        })}
      </Text>
      {isAdmin ? <MnemonicSuggestionButton /> : null}
    </LongPressable>
  );
}
